import React from 'react';
import { Box, Typography } from '@mui/material';
import { colors } from '../../styles/theme';
import logoTerrioBlanco from '../../Map/assets/Logo-Terrio-blanco.png';
import logoTerrioNegro from '../../assets/branding/logo-terrio.png';
import { formatMetaMoney } from '../metaAdsMoney';
import { metaRemainingLabel } from '../metaBudget';
import { formatMetaCount, formatMetaPct, formatMetaSeconds, metaPlural } from '../metaFormat';
import { formatMetaDateTime, formatMetaDay, formatMetaPeriod } from '../metaPeriod';

/**
 * El informe de una propiedad (mismo documento para la vista previa, la vista imprimible y el link
 * público). `tone="dark"` = dentro del panel; `tone="paper"` = fondo blanco para imprimir / el link.
 * Siempre con gasto y costos (decisión de producto). Recibe el modelo de `readUnitReport`.
 */
const TONES = {
  dark: {
    text: '#F2F2F2', muted: 'rgba(255,255,255,0.62)', card: '#262626', border: 'rgba(255,255,255,0.1)', tile: '#303030',
    accent: colors.primaryLight, bar: colors.primary, track: 'rgba(255,255,255,0.1)', note: 'rgba(255,255,255,0.06)', head: '#FFFFFF',
    logo: logoTerrioBlanco,
  },
  paper: {
    text: '#1d2327', muted: '#5f6b73', card: '#FFFFFF', border: '#dfe4e8', tile: '#FFFFFF',
    accent: '#2f7f79', bar: '#4a9a94', track: '#e8ecee', note: '#f3f5f6', head: '#111111',
    // Vista pública / PDF (papel blanco): el logo negro que ya está en el repo.
    logo: logoTerrioNegro,
  },
};

const NO_ADS_TEXT = 'Todavía no hay anuncios para esta propiedad.';
const DEFAULT_ALLOCATION_NOTE = 'Si un anuncio promociona varias propiedades, su gasto se reparte en partes iguales entre ellas.';
const HISTORY_START_NOTE = (since) => `Historial desde el ${since ? formatMetaDay(since, { withYear: true }) : '27 oct 2026'}: antes de esa fecha no hay nada guardado.`;

const money = (v, cur) => (v == null ? '—' : formatMetaMoney(v, cur));

function Section({ t, title, children, testId, aside = null }) {
  return (
    <Box
      component="section"
      aria-label={title}
      data-testid={testId}
      sx={{ border: `1px solid ${t.border}`, bgcolor: t.card, borderRadius: '10px', p: 2, display: 'flex', flexDirection: 'column', gap: 1.25, breakInside: 'avoid', pageBreakInside: 'avoid' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography component="h3" sx={{ fontSize: '15px', fontWeight: 700, color: t.head, flex: 1, minWidth: 0 }}>{title}</Typography>
        {aside}
      </Box>
      {children}
    </Box>
  );
}

function Tile({ t, value, label, hint = null, testId }) {
  return (
    <Box sx={{ border: `1px solid ${t.border}`, bgcolor: t.tile, borderRadius: '8px', px: 1.5, py: 1.1, minWidth: 0 }} data-testid={testId}>
      <Typography sx={{ fontSize: '20px', fontWeight: 700, color: t.head, lineHeight: 1.2, overflowWrap: 'anywhere' }}>{value}</Typography>
      <Typography sx={{ fontSize: '12px', color: t.muted }}>{label}</Typography>
      {hint ? <Typography sx={{ fontSize: '11.5px', color: t.muted, mt: 0.25 }}>{hint}</Typography> : null}
    </Box>
  );
}

const tilesSx = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1 };

function Note({ t, children, testId, tone = 'neutral' }) {
  return (
    <Typography
      sx={{ fontSize: '12.5px', lineHeight: 1.45, color: t.muted, bgcolor: tone === 'strong' ? t.note : 'transparent', borderRadius: '6px', px: tone === 'strong' ? 1.25 : 0, py: tone === 'strong' ? 0.9 : 0 }}
      data-testid={testId}
    >
      {children}
    </Typography>
  );
}

function Badge({ t, children, testId }) {
  return (
    <Box component="span" data-testid={testId} sx={{ fontSize: '11.5px', fontWeight: 600, px: 1, py: '1px', borderRadius: '999px', border: `1px solid ${t.border}`, color: t.muted, whiteSpace: 'nowrap' }}>
      {children}
    </Box>
  );
}

function Rows({ t, rows, testId }) {
  return (
    <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) max-content' }} data-testid={testId}>
      {rows.filter(Boolean).map((r) => (
        <Box key={r.key} sx={{ display: 'contents' }} data-testid={testId ? `${testId}-${r.key}` : undefined}>
          <Box component="dt" sx={{ fontSize: '13px', color: t.text, py: 0.7, pr: 2, borderBottom: `1px solid ${t.border}` }}>{r.label}</Box>
          <Box component="dd" sx={{ fontSize: '13px', fontWeight: 600, color: t.head, m: 0, py: 0.7, textAlign: 'right', whiteSpace: 'nowrap', borderBottom: `1px solid ${t.border}` }}>{r.value}</Box>
        </Box>
      ))}
    </Box>
  );
}

function Bars({ t, rows, testId, unit = 'contactos' }) {
  const max = Math.max(1, ...rows.map((r) => r.contacts || 0));
  return (
    <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gridTemplateColumns: 'minmax(56px, max-content) minmax(40px, 1fr) max-content', columnGap: 1.25, rowGap: 0.9, alignItems: 'center' }} data-testid={testId}>
      {rows.map((r) => (
        <Box component="li" key={r.key} sx={{ display: 'contents' }} data-testid={`${testId}-${r.key}`}>
          <Typography sx={{ fontSize: '13px', color: t.text }}>{r.label}</Typography>
          <Box sx={{ height: 8, borderRadius: '4px', bgcolor: t.track, overflow: 'hidden' }} aria-hidden>
            <Box sx={{ height: '100%', width: `${Math.max(0, Math.min(1, (r.contacts || 0) / max)) * 100}%`, bgcolor: t.bar, borderRadius: '4px', printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' }} />
          </Box>
          <Typography sx={{ fontSize: '13px', color: t.text, textAlign: 'right', whiteSpace: 'nowrap' }}>
            {`${formatMetaCount(r.contacts)} ${Number(r.contacts) === 1 ? unit.replace(/s$/, '') : unit}${r.shareOfContactsPct != null ? ` · ${formatMetaPct(r.shareOfContactsPct)}` : ''}`}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

const inline = (rows, max = 4) => rows.filter((r) => r.contacts).slice(0, max)
  .map((r) => `${r.label} ${r.shareOfContactsPct != null ? formatMetaPct(r.shareOfContactsPct) : metaPlural(r.contacts, 'contacto', 'contactos')}`)
  .join(' · ');

/** Regiones sin contactos completos: por impresiones (% de impresiones o cantidad). */
const inlineByImpressions = (rows, max = 4) => [...rows].filter((r) => r.impressions)
  .sort((x, y) => (y.impressions || 0) - (x.impressions || 0)).slice(0, max)
  .map((r) => `${r.label} ${r.shareOfImpressionsPct != null ? formatMetaPct(r.shareOfImpressionsPct) : `${formatMetaCount(r.impressions)} impresiones`}`)
  .join(' · ');

function Summary({ t, report }) {
  const { summary, reach, spend } = report;
  const cur = spend?.currency;
  return (
    <Section t={t} title="Resumen" testId="unit-report-summary">
      <Box sx={tilesSx}>
        <Tile
          t={t}
          testId="unit-report-contacts"
          value={formatMetaCount(summary.contacts)}
          label="Contactos"
          hint={summary.contacts != null && (summary.waConversations != null || summary.leads != null)
            ? `${metaPlural(summary.waConversations || 0, 'conversación de WhatsApp', 'conversaciones de WhatsApp')} + ${metaPlural(summary.leads || 0, 'formulario', 'formularios')}`
            : null}
        />
        {/* Alcance real (personas sin repetir). Sin número: el motivo, nunca un error ni un número inventado. */}
        <Tile
          t={t}
          testId="unit-report-reach"
          value={reach.value != null ? formatMetaCount(reach.value) : 'No disponible'}
          label="Alcance (personas distintas)"
          hint={reach.value != null
            ? [reach.stale ? 'Dato guardado, puede estar desactualizado' : null, summary.frequency != null ? `Frecuencia ${String(summary.frequency).replace('.', ',')}` : null].filter(Boolean).join(' · ') || null
            : reach.unavailableText}
        />
        <Tile t={t} testId="unit-report-impressions" value={formatMetaCount(summary.impressions)} label="Impresiones" />
        <Tile t={t} testId="unit-report-linkclicks" value={formatMetaCount(summary.linkClicks)} label="Clics en el enlace" />
        {spend ? (
          <>
            <Tile
              t={t}
              testId="unit-report-spend"
              value={spend.mixedCurrencies ? 'Monedas distintas' : money(spend.inPeriod, cur)}
              label="Gasto en el período"
              hint={spend.mixedCurrencies ? 'Detalle por moneda más abajo' : null}
            />
            <Tile
              t={t}
              testId="unit-report-cost-per-contact"
              value={spend.mixedCurrencies ? '—' : money(spend.costPerContact, cur)}
              label="Costo por contacto"
            />
          </>
        ) : null}
      </Box>
      <Note t={t} testId="unit-report-counts">
        {`${metaPlural(summary.activeAdsCount ?? 0, 'anuncio activo', 'anuncios activos')} de ${formatMetaCount(summary.adsCount ?? 0)} · ${metaPlural(summary.campaignsCount ?? 0, 'campaña', 'campañas')}`}
      </Note>
    </Section>
  );
}

function SpendSection({ t, report }) {
  const { spend } = report;
  if (!spend) return null;
  const cur = spend.currency;
  const note = spend.allocationApplied ? (spend.allocationNote || DEFAULT_ALLOCATION_NOTE) : null;
  const rows = spend.mixedCurrencies ? [] : [
    { key: 'period', label: 'Gasto en el período', value: money(spend.inPeriod, cur) },
    { key: 'lifetime', label: spend.lifetimeComplete ? 'Gasto desde el inicio' : 'Gasto desde el inicio (parcial)', value: money(spend.lifetime, cur) },
    { key: 'cpc', label: 'Costo por contacto', value: money(spend.costPerContact, cur) },
    { key: 'cplc', label: 'Costo por clic en el enlace', value: money(spend.costPerLinkClick, cur) },
    spend.costPerEngagement != null ? { key: 'cpe', label: 'Costo por interacción', value: money(spend.costPerEngagement, cur) } : null,
    spend.cpm != null ? { key: 'cpm', label: 'Costo por mil impresiones', value: money(spend.cpm, cur) } : null,
  ];
  return (
    <Section t={t} title="Gasto y costos" testId="unit-report-spend-section">
      {spend.mixedCurrencies ? (
        <>
          <Note t={t}>Los anuncios están en cuentas de distinta moneda: cada moneda va por separado y nunca se suman.</Note>
          {spend.byCurrency.map((c) => (
            <Rows
              key={c.currency}
              t={t}
              testId={`unit-report-spend-${c.currency}`}
              rows={[
                { key: 'period', label: `Gasto en el período · ${c.currency}`, value: money(c.inPeriod, c.currency) },
                { key: 'lifetime', label: `Gasto desde el inicio · ${c.currency}`, value: money(c.lifetime, c.currency) },
                { key: 'cpc', label: `Costo por contacto · ${c.currency}`, value: money(c.costPerContact, c.currency) },
              ]}
            />
          ))}
        </>
      ) : <Rows t={t} rows={rows} testId="unit-report-spend-rows" />}
      {note ? <Note t={t} tone="strong" testId="unit-report-allocation-note">{note}</Note> : null}
    </Section>
  );
}

function Series({ t, report }) {
  const rows = report.series;
  if (!rows.length) return null;
  const max = Math.max(1, ...rows.map((r) => r.contacts || 0));
  return (
    <Section t={t} title="Contactos por semana" testId="unit-report-series">
      <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gridTemplateColumns: 'minmax(96px, max-content) minmax(40px, 1fr) max-content max-content', columnGap: 1.25, rowGap: 0.9, alignItems: 'center' }}>
        {rows.map((r) => (
          <Box component="li" key={r.key} sx={{ display: 'contents' }} data-testid={`unit-report-series-${r.key}`}>
            <Typography sx={{ fontSize: '13px', color: t.text }}>{`${formatMetaDay(r.start)} – ${formatMetaDay(r.end)}`}</Typography>
            <Box sx={{ height: 8, borderRadius: '4px', bgcolor: t.track, overflow: 'hidden' }} aria-hidden>
              <Box sx={{ height: '100%', width: `${((r.contacts || 0) / max) * 100}%`, bgcolor: t.bar, borderRadius: '4px', printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' }} />
            </Box>
            <Typography sx={{ fontSize: '13px', color: t.text, textAlign: 'right', whiteSpace: 'nowrap' }}>{metaPlural(r.contacts ?? 0, 'contacto', 'contactos')}</Typography>
            <Typography sx={{ fontSize: '12px', color: t.muted, textAlign: 'right', whiteSpace: 'nowrap' }}>{`${formatMetaCount(r.impressions)} impresiones`}</Typography>
          </Box>
        ))}
      </Box>
    </Section>
  );
}

function Interactions({ t, report }) {
  const i = report.interactions;
  if (!i) return null;
  const cur = report.spend?.currency;
  const v = i.video;
  const rows = [
    i.total != null ? { key: 'total', label: 'Interacciones con la publicación', value: formatMetaCount(i.total) } : null,
    i.reactions != null ? { key: 'reactions', label: 'Reacciones', value: formatMetaCount(i.reactions) } : null,
    i.comments != null ? { key: 'comments', label: 'Comentarios', value: formatMetaCount(i.comments) } : null,
    i.shares != null ? { key: 'shares', label: 'Veces compartida', value: formatMetaCount(i.shares) } : null,
    i.saves != null ? { key: 'saves', label: 'Veces guardada', value: formatMetaCount(i.saves) } : null,
    i.pageLikes != null ? { key: 'pageLikes', label: 'Me gusta de la página', value: formatMetaCount(i.pageLikes) } : null,
    i.clicks != null ? { key: 'clicks', label: 'Clics (todos)', value: formatMetaCount(i.clicks) } : null,
    i.outboundClicks != null ? { key: 'outbound', label: 'Clics salientes', value: formatMetaCount(i.outboundClicks) } : null,
    i.landingPageViews != null ? { key: 'lpv', label: 'Visitas a la página de destino', value: formatMetaCount(i.landingPageViews) } : null,
    report.spend && !report.spend.mixedCurrencies && i.costPerEngagement != null ? { key: 'cpe', label: 'Costo por interacción', value: money(i.costPerEngagement, cur) } : null,
    v.views3s ? { key: 'v3', label: 'Reproducciones de 3 segundos', value: formatMetaCount(v.views3s) } : null,
    v.thruplays ? { key: 'thru', label: 'Reproducciones ThruPlay (video completo o 15 s)', value: formatMetaCount(v.thruplays) } : null,
    v.avgWatchS != null && v.plays ? { key: 'avg', label: 'Tiempo promedio de reproducción', value: formatMetaSeconds(v.avgWatchS) } : null,
    v.hookRatePct != null && v.views3s ? { key: 'hook', label: 'Tasa de reproducciones de 3 segundos sobre impresiones', value: formatMetaPct(v.hookRatePct) } : null,
  ];
  return (
    <Section t={t} title="Interacciones" testId="unit-report-interactions">
      <Rows t={t} rows={rows} testId="unit-report-interactions-rows" />
    </Section>
  );
}

function Audience({ t, report }) {
  const a = report.audience;
  if (!a) return null;
  const hasAny = a.age.length || a.gender.length || a.platform.length || a.region.length || a.country.length;
  return (
    <Section
      t={t}
      title={a.label}
      testId="unit-report-audience"
      aside={a.approximate ? <Badge t={t} testId="unit-report-audience-approx">Aproximado</Badge> : null}
    >
      {a.approximate ? (
        <Note t={t} tone="strong" testId="unit-report-audience-approx-note">
          {a.approximateReason || 'El público es aproximado: Meta lo informa por campaña, no por propiedad. Incluye a personas que respondieron a otros anuncios de las mismas campañas.'}
        </Note>
      ) : null}
      {!hasAny ? <Note t={t} testId="unit-report-audience-empty">Todavía no hay datos de público para este período.</Note> : null}
      {a.age.length ? <Bars t={t} rows={a.age} testId="unit-report-audience-age" /> : null}
      {a.gender.length ? <Note t={t} testId="unit-report-audience-gender">{`Género: ${inline(a.gender, 3)}`}</Note> : null}
      {a.platform.length ? <Note t={t} testId="unit-report-audience-platform">{`Plataforma: ${inline(a.platform, 4)}`}</Note> : null}
      {a.country.length ? <Note t={t} testId="unit-report-audience-country">{`País: ${inline(a.country, 3)}`}</Note> : null}
      {a.region.length && a.regionContactsComplete ? <Note t={t} testId="unit-report-audience-region">{`Región: ${inline(a.region, 4)}`}</Note> : null}
      {a.region.length && !a.regionContactsComplete ? (
        <>
          <Note t={t} testId="unit-report-audience-region">{`Región (impresiones): ${inlineByImpressions(a.region, 4)}`}</Note>
          <Note t={t} testId="unit-report-audience-region-note">{a.regionContactsNote || 'Meta no informa todos los contactos por región: se muestran las impresiones.'}</Note>
        </>
      ) : null}
      {a.limited ? <Note t={t} testId="unit-report-audience-limited">{a.limitedNote || 'Meta limita la edad y el género en los anuncios de viviendas: puede haber menos datos.'}</Note> : null}
      {a.coverageNotes.map((n) => <Note key={n} t={t}>{n}</Note>)}
      <Note t={t}>Es solo informativo: no se usa para segmentar anuncios.</Note>
    </Section>
  );
}

function Campaigns({ t, report }) {
  if (!report.campaigns.length) return null;
  return (
    <Section t={t} title="Campañas" testId="unit-report-campaigns">
      <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 1 }}>
        {report.campaigns.map((c) => {
          const b = c.budget;
          const budget = b && (b.daily != null || b.lifetime != null)
            ? [
              b.daily != null ? `Presupuesto diario ${money(b.daily, b.currency)}` : null,
              b.lifetime != null ? `Presupuesto total ${money(b.lifetime, b.currency)}` : null,
              // Qué es el restante (27/10): lo que queda hoy del diario o lo que queda del total; sin scope, no se muestra.
              b.remaining != null && b.remainingScope ? `${metaRemainingLabel(b.remainingScope)} ${money(b.remaining, b.currency)}` : null,
            ].filter(Boolean).join(' · ')
            : null;
          return (
            <Box component="li" key={c.key} data-testid={`unit-report-campaign-${c.key}`} sx={{ borderBottom: `1px solid ${t.border}`, pb: 1 }}>
              <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: t.head, overflowWrap: 'anywhere' }}>{c.name}</Typography>
              <Typography sx={{ fontSize: '12.5px', color: t.muted }}>
                {[c.deliveryLabel,
                  c.adsInReport != null ? `${metaPlural(c.adsInReport, 'anuncio', 'anuncios')} de esta propiedad${c.hasOtherAds && c.campaignAdsCount != null ? ` (la campaña tiene ${formatMetaCount(c.campaignAdsCount)})` : ''}` : null,
                  budget].filter(Boolean).join(' · ')}
              </Typography>
              {b?.note ? <Typography sx={{ fontSize: '12px', color: t.muted }}>{b.note}</Typography> : null}
            </Box>
          );
        })}
      </Box>
    </Section>
  );
}

function Ads({ t, report }) {
  const { ads, status } = report;
  return (
    <Section t={t} title={`Anuncios de esta propiedad${ads.length ? ` (${ads.length})` : ''}`} testId="unit-report-ads">
      {status === 'no_ads' || !ads.length ? <Note t={t} testId="unit-report-no-ads">{NO_ADS_TEXT}</Note> : null}
      <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {ads.map((ad) => {
          const m = ad.metrics;
          const sp = ad.spend;
          return (
            <Box component="li" key={ad.key} data-testid={`unit-report-ad-${ad.key}`} sx={{ display: 'grid', gridTemplateColumns: '48px minmax(0, 1fr)', columnGap: 1.25, alignItems: 'start', borderBottom: `1px solid ${t.border}`, pb: 1.25, breakInside: 'avoid' }}>
              <Box sx={{ width: 48, height: 48, borderRadius: '6px', overflow: 'hidden', bgcolor: t.track }} aria-hidden>
                {ad.thumbnailUrl ? <Box component="img" src={ad.thumbnailUrl} alt="" loading="lazy" referrerPolicy="no-referrer" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} /> : null}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: t.head, overflowWrap: 'anywhere' }}>{ad.name}</Typography>
                <Typography sx={{ fontSize: '12px', color: t.muted, overflowWrap: 'anywhere' }}>
                  {[ad.campaignName, ad.deliveryLabel].filter(Boolean).join(' · ')}
                </Typography>
                <Typography sx={{ fontSize: '12.5px', color: t.text, mt: 0.25 }}>
                  {[`${metaPlural(m.contacts ?? 0, 'contacto', 'contactos')}`, `${formatMetaCount(m.impressions)} impresiones`, `${formatMetaCount(m.linkClicks)} clics en el enlace`].join(' · ')}
                </Typography>
                {sp ? (
                  <Typography sx={{ fontSize: '12.5px', color: t.text }} data-testid={`unit-report-ad-${ad.key}-spend`}>
                    {[`Gasto ${money(sp.inPeriod, sp.currency)}`, sp.costPerContact != null ? `${money(sp.costPerContact, sp.currency)} por contacto` : null].filter(Boolean).join(' · ')}
                  </Typography>
                ) : null}
                {ad.isShared ? (
                  <Typography sx={{ fontSize: '12px', color: t.muted }} data-testid={`unit-report-ad-${ad.key}-shared`}>
                    {`Anuncio compartido con ${ad.sharedUnitsCount != null ? `${formatMetaCount(ad.sharedUnitsCount)} propiedades` : 'otras propiedades'}: ${sp && sp.allocation === 'equal_split' ? `el gasto se reparte en partes iguales (este anuncio gastó ${money(sp.adTotalInPeriod, sp.currency)} en total)` : 'las cantidades son las del anuncio completo'}.`}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Section>
  );
}

function Inquiries({ t, report }) {
  const q = report.inquiries;
  if (!q) return null;
  const rows = [
    { key: 'period', label: 'Consultas en el período', value: formatMetaCount(q.inPeriod) },
    { key: 'total', label: 'Consultas en total', value: formatMetaCount(q.total) },
    q.interestedPeople != null ? { key: 'people', label: 'Personas interesadas', value: formatMetaCount(q.interestedPeople) } : null,
    q.lastDate ? { key: 'last', label: 'Última consulta', value: formatMetaDay(q.lastDate, { withYear: true }) || q.lastDate } : null,
  ];
  return (
    <Section t={t} title="Consultas en Terrio" testId="unit-report-inquiries">
      <Rows t={t} rows={rows} testId="unit-report-inquiries-rows" />
      {q.byOrigin.some((o) => o.value) ? (
        <Note t={t} testId="unit-report-inquiries-origin">{`Origen en el período: ${q.byOrigin.filter((o) => o.value).map((o) => `${o.label} ${formatMetaCount(o.value)}`).join(' · ')}`}</Note>
      ) : null}
      {q.byStage.some((o) => o.value) ? (
        <Note t={t} testId="unit-report-inquiries-stage">{`Etapa de las personas interesadas: ${q.byStage.filter((o) => o.value).map((o) => `${o.label} ${formatMetaCount(o.value)}`).join(' · ')}`}</Note>
      ) : null}
      <Note t={t}>Son cantidades reales; no se muestran nombres ni datos de contacto de nadie.</Note>
    </Section>
  );
}

const changeText = (c) => [
  c.price ? `Precio: ${money(c.price.from, c.price.currency)} → ${money(c.price.to, c.price.currency)}` : null,
  c.status ? `Estado: ${c.status.from || '—'} → ${c.status.to || '—'}` : null,
  c.operation ? `Operación: ${c.operation.from || '—'} → ${c.operation.to || '—'}` : null,
].filter(Boolean).join(' · ');

function History({ t, report }) {
  const h = report.history;
  if (!h) return null;
  return (
    <Section t={t} title="Historial de precio y estado" testId="unit-report-history">
      <Note t={t} testId="unit-report-history-since">{HISTORY_START_NOTE(h.availableSince)}</Note>
      {h.changes.length === 0 ? <Note t={t} testId="unit-report-history-empty">Sin cambios de precio ni de estado desde entonces.</Note> : (
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {h.changes.map((c) => (
            <Box component="li" key={c.key} data-testid={`unit-report-history-${c.key}`} sx={{ fontSize: '13px', color: t.text }}>
              <Box component="span" sx={{ fontWeight: 600 }}>{formatMetaDay(c.date, { withYear: true }) || c.date}</Box>
              {` · ${changeText(c)}`}
            </Box>
          ))}
        </Box>
      )}
    </Section>
  );
}

/**
 * Encabezado: siempre el logo de Terrio (blanco en oscuro, negro en papel); el logo del usuario no se
 * muestra (decisión 28/10). La identidad del agente (`agent_branding`) queda como una línea de
 * contacto: nombre · teléfono · WhatsApp · email (lo que exista).
 */
function Header({ t, report, tone, showLogo = true }) {
  const { branding, unit, period, freshness } = report;
  const facts = [unit.operationLabel, unit.statusLabel, unit.price != null ? money(unit.price, unit.currency) : null].filter(Boolean).join(' · ');
  const contact = branding
    ? [branding.name, branding.phone, branding.whatsapp && branding.whatsapp !== branding.phone ? `WhatsApp ${branding.whatsapp}` : null, branding.email].filter(Boolean).join(' · ')
    : '';
  return (
    <Box component="header" sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }} data-testid="unit-report-header">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: showLogo ? 'space-between' : 'flex-start', gap: 1.25, flexWrap: 'wrap', mb: 0.5 }}>
        {showLogo ? <Box component="img" src={t.logo} alt="Terrio" sx={{ height: 22, width: 'auto', display: 'block' }} data-testid="unit-report-logo" /> : null}
        <Typography sx={{ fontSize: '12.5px', color: t.muted }}>
          {`Informe de anuncios${report.generatedAt ? ` · generado el ${formatMetaDateTime(report.generatedAt)}` : ''}`}
        </Typography>
      </Box>
      <Typography component="h2" sx={{ fontSize: tone === 'paper' ? '26px' : '20px', fontWeight: 700, color: t.head, lineHeight: 1.2, overflowWrap: 'anywhere' }} data-testid="unit-report-title">
        {[unit.label || 'Propiedad', unit.projectLabel].filter(Boolean).join(' · ')}
      </Typography>
      {facts ? <Typography sx={{ fontSize: '13px', color: t.muted }} data-testid="unit-report-facts">{facts}</Typography> : null}
      <Typography sx={{ fontSize: '13px', color: t.muted }} data-testid="unit-report-period">
        {`Período: ${formatMetaPeriod(period) || '—'} · Anuncios en Facebook e Instagram (Meta)`}
      </Typography>
      {freshness.stale && freshness.text ? <Typography sx={{ fontSize: '12.5px', color: t.muted }} data-testid="unit-report-freshness">{freshness.text}</Typography> : null}
      {contact ? <Typography sx={{ fontSize: '12.5px', color: t.muted, overflowWrap: 'anywhere' }} data-testid="unit-report-contact">{contact}</Typography> : null}
    </Box>
  );
}

/**
 * @param {{ report: object, tone?: 'dark'|'paper', showLogo?: boolean }} props `report` = `readUnitReport(res)`.
 *   `showLogo={false}`: dentro del panel del link con mapa, donde la barra de arriba ya lleva el logo de Terrio.
 */
export default function UnitReportDocument({ report, tone = 'dark', showLogo = true }) {
  const t = TONES[tone] || TONES.dark;
  const notes = [...new Set([report.spend?.allocationApplied ? (report.spend.allocationNote || DEFAULT_ALLOCATION_NOTE) : null, ...report.notes].filter(Boolean))];
  return (
    <Box
      component="article"
      data-testid="unit-report-document"
      data-tone={tone}
      sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, color: t.text, bgcolor: tone === 'paper' ? '#FFFFFF' : 'transparent', fontFamily: 'inherit', '@media print': { gap: 1.25 } }}
    >
      <Header t={t} report={report} tone={tone} showLogo={showLogo} />
      {report.status === 'no_ads' ? (
        <Section t={t} title="Anuncios de esta propiedad" testId="unit-report-no-ads-section">
          <Note t={t} testId="unit-report-no-ads">{NO_ADS_TEXT}</Note>
        </Section>
      ) : (
        <>
          <Summary t={t} report={report} />
          <SpendSection t={t} report={report} />
          <Series t={t} report={report} />
          <Interactions t={t} report={report} />
          <Audience t={t} report={report} />
          <Campaigns t={t} report={report} />
          <Ads t={t} report={report} />
          <Inquiries t={t} report={report} />
          <History t={t} report={report} />
        </>
      )}
      {report.status === 'no_ads' ? <><Inquiries t={t} report={report} /><History t={t} report={report} /></> : null}
      <Box component="footer" sx={{ borderTop: `1px solid ${t.border}`, pt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }} data-testid="unit-report-footer">
        {notes.map((n) => <Typography key={n} sx={{ fontSize: '12px', color: t.muted }}>{n}</Typography>)}
        <Typography sx={{ fontSize: '12px', color: t.muted }}>
          {report.generatedAt ? `Datos de Meta al ${formatMetaDateTime(report.generatedAt)} (hora de Córdoba)` : 'Datos de Meta'}
        </Typography>
      </Box>
    </Box>
  );
}
